import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Save, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import AuthModal from './AuthModal';

const UserButton = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isAuthenticated) {
    return (
      <>
        <button
          onClick={() => setShowAuthModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#C4B5A0] text-white rounded-lg hover:bg-[#6B4E37] transition-colors text-sm font-medium"
          data-testid="login-btn"
        >
          <User size={16} />
          <span className="hidden md:inline">{t('nav.login')}</span>
        </button>
        
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
        />
      </>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm rounded-lg hover:bg-white transition-colors"
        data-testid="user-menu-btn"
      >
        {user.picture ? (
          <img 
            src={user.picture} 
            alt={user.name} 
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#C4B5A0] flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {user.name?.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <span className="hidden md:inline text-sm text-[#2C2C2C] max-w-[100px] truncate">
          {user.name?.split(' ')[0]}
        </span>
        <ChevronDown size={14} className="text-[#8B7D6B]" />
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-[#E8E4E0] overflow-hidden z-50 animate-fade-in">
          {/* User Info */}
          <div className="p-4 border-b border-[#E8E4E0]">
            <p className="font-medium text-[#2C2C2C] truncate">{user.name}</p>
            <p className="text-xs text-[#8B7D6B] truncate">{user.email}</p>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={() => {
                setShowDropdown(false);
                // Navigate to saved progress or show it
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#6B4E37] hover:bg-[#FAF9F7] transition-colors"
            >
              <Save size={16} />
              {t('nav.login') === 'Login' ? 'My progress' : 'O meu progresso'}
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-[#E8E4E0] py-2">
            <button
              onClick={() => {
                setShowDropdown(false);
                logout();
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              {t('nav.login') === 'Login' ? 'Logout' : 'Sair'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserButton;
